import AnimatedModal from "@/components/base/AnimatedModal";
import type { AppUser } from "@/context/AuthContext";

interface Experience {
  id: string;
  company: string;
  position: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  description: string;
}

interface Education {
  id: string;
  school: string;
  degree: string;
  field: string;
  graduation_year: string;
}

interface Certification {
  id: string;
  name: string;
  issuer: string;
  year: string;
}

interface Language {
  id: string;
  name: string;
  level: string;
}

interface CVFormData {
  title: string;
  address: string;
  birth_date: string;
  gender: string;
  website: string;
  summary: string;
  experience: Experience[];
  education: Education[];
  skills: string;
  certifications: Certification[];
  languages: Language[];
}

interface CVPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AppUser;
  form: CVFormData;
}

export default function CVPreviewModal({ isOpen, onClose, user, form }: CVPreviewModalProps) {
  const expList = form.experience.filter((e) => e.company || e.position);
  const eduList = form.education.filter((e) => e.school || e.degree);
  const certList = form.certifications.filter((c) => c.name);
  const langList = form.languages.filter((l) => l.name);
  const skillList = form.skills.split(",").map((s) => s.trim()).filter(Boolean);

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} variant="fade-up">
      <div className="relative w-[92vw] max-w-[800px] bg-white rounded-2xl shadow-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="sticky top-0 bg-white border-b border-background-200/70 px-6 py-4 flex items-center justify-between z-10 flex-shrink-0">
          <h3 className="font-heading font-semibold text-lg text-foreground-900">
            Xem trước CV
          </h3>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg text-foreground-600"></i>
          </button>
        </div>

        <div className="p-8 overflow-y-auto flex-1" style={{ fontFamily: "'Lexend', Arial, sans-serif" }}>
          {/* Header */}
          <div className="mb-8 pb-5 border-b-[3px] border-accent-600">
            <h1 className="text-2xl font-bold text-foreground-950 mb-1">{user.name}</h1>
            {form.title && (
              <p className="text-sm text-accent-600 font-semibold mb-2">{form.title}</p>
            )}
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-foreground-600">
              {user.email && (
                <span className="flex items-center gap-1">
                  <i className="ri-mail-line text-accent-500"></i>
                  {user.email}
                </span>
              )}
              {user.phone && (
                <span className="flex items-center gap-1">
                  <i className="ri-phone-line text-accent-500"></i>
                  {user.phone}
                </span>
              )}
              {form.address && (
                <span className="flex items-center gap-1">
                  <i className="ri-map-pin-line text-accent-500"></i>
                  {form.address}
                </span>
              )}
              {form.website && (
                <span className="flex items-center gap-1">
                  <i className="ri-link text-accent-500"></i>
                  {form.website}
                </span>
              )}
            </div>
          </div>

          {/* Summary */}
          {form.summary && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-foreground-950 uppercase tracking-wide mb-3 pb-1.5 border-b border-background-200">
                Giới thiệu
              </h3>
              <p className="text-sm text-foreground-700 leading-relaxed">{form.summary}</p>
            </div>
          )}

          {/* Experience */}
          {expList.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-foreground-950 uppercase tracking-wide mb-3 pb-1.5 border-b border-background-200">
                Kinh nghiệm làm việc
              </h3>
              {expList.map((exp) => (
                <div key={exp.id} className="mb-4">
                  <div className="flex items-start justify-between gap-2">
                    <strong className="text-sm text-foreground-900">{exp.position}</strong>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">
                      {exp.start_date} - {exp.is_current ? "Hiện tại" : exp.end_date}
                    </span>
                  </div>
                  <p className="text-xs text-accent-600 font-medium mt-0.5">{exp.company}</p>
                  {exp.description && (
                    <p className="text-xs text-foreground-600 mt-1.5 leading-relaxed">{exp.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Education */}
          {eduList.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-foreground-950 uppercase tracking-wide mb-3 pb-1.5 border-b border-background-200">
                Học vấn
              </h3>
              {eduList.map((edu) => (
                <div key={edu.id} className="mb-3">
                  <strong className="text-sm text-foreground-900">
                    {edu.degree}{edu.field ? ` - ${edu.field}` : ""}
                  </strong>
                  <p className="text-xs text-accent-600 font-medium mt-0.5">
                    {edu.school}{edu.graduation_year ? ` (${edu.graduation_year})` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Skills */}
          {skillList.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-foreground-950 uppercase tracking-wide mb-3 pb-1.5 border-b border-background-200">
                Kỹ năng
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {skillList.map((s, i) => (
                  <span key={i} className="px-2.5 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {certList.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-foreground-950 uppercase tracking-wide mb-3 pb-1.5 border-b border-background-200">
                Chứng chỉ
              </h3>
              {certList.map((cert) => (
                <div key={cert.id} className="mb-2 text-sm text-foreground-700">
                  <strong>{cert.name}</strong>
                  {cert.issuer && ` - ${cert.issuer}`}
                  {cert.year && ` (${cert.year})`}
                </div>
              ))}
            </div>
          )}

          {/* Languages */}
          {langList.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-foreground-950 uppercase tracking-wide mb-3 pb-1.5 border-b border-background-200">
                Ngôn ngữ
              </h3>
              {langList.map((lang) => (
                <div key={lang.id} className="mb-1.5 text-sm text-foreground-700">
                  <strong>{lang.name}</strong> - {lang.level}
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!form.summary && expList.length === 0 && eduList.length === 0 && skillList.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-file-text-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-sm text-foreground-500">CV chưa có nội dung. Hãy bổ sung thông tin để xem trước.</p>
            </div>
          )}
        </div>
      </div>
    </AnimatedModal>
  );
}